import type { QueryObserverModel } from '../QueryObserverModel';
import type { QueryObserverOptions } from '@tanstack/query-core';
import { inject, injectable, ReactiveInjectable, reacts } from 'src/features/ioc';
import type { LibraryInitOptions, LibraryQueryResult, LibraryModel } from 'src/models/LibraryModel';
import type { ReactBindings } from 'src/types/bindings';

interface LibraryModelState<T> {
    error: Error | undefined;
    isFetching: boolean;
    isLoading: boolean;
    rows: T[];
    total: number;
    selectedRow: T | undefined;
}

@injectable()
export class LibraryModelImpl<T> extends ReactiveInjectable implements LibraryModel<T> {
    constructor(
        @inject('QueryObserverModelFactory')
        private queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'],
    ) {
        // eslint-disable-next-line prefer-rest-params -- forwarding constructor args
        super(...arguments);
    }

    init({ queryOptions }: LibraryInitOptions<T>) {
        this.#queryObserverModel = this.queryObserverModelFactory(queryOptions);

        const disposable = this.#queryObserverModel.subscribe((result) => {
            this.state = {
                error: result.error ?? undefined,
                isFetching: result.isFetching,
                isLoading: result.isLoading,
                rows: result.data?.rows ?? [],
                total: result.data?.total ?? 0,
                selectedRow: undefined,
            };
        });

        this.disposableStack.use(disposable);
    }

    setSelectedRow(row: T) {
        this.state = {
            ...this.#state,
            selectedRow: row,
        };
    }

    clearSelectedRow() {
        this.state = {
            ...this.#state,
            selectedRow: undefined,
        };
    }

    get isFetching() {
        return this.#state.isFetching;
    }

    get isLoading() {
        return this.#state.isLoading;
    }

    get rows() {
        return this.#state.rows;
    }

    get selectedRow() {
        return this.#state.selectedRow;
    }

    #queryObserverModel!: QueryObserverModel<LibraryQueryResult<T>>;

    setQueryOptions(queryOptions: QueryObserverOptions<LibraryQueryResult<T>>) {
        this.#queryObserverModel.setQueryOptions(queryOptions);
    }

    #state: LibraryModelState<T> = {
        error: undefined,
        isFetching: false,
        isLoading: true,
        rows: [],
        total: 0,
        selectedRow: undefined,
    };

    @reacts set state(state: LibraryModelState<T>) {
        this.#state = state;
    }

    get total() {
        return this.#state.total;
    }
}
